// The source-issues queue (E32-S8-T4; ADR-031), built from the approved mockup
// mockups/issues-queue.html (T0 approved it in #495): the counts that are also the filters, the
// filters, the list, and the answer panel beside it (under it on a narrow screen). The client
// reviewer answers or waives; the guide's owners read, and may reopen with a follow-up.
//
// Everything the page shows comes from GET /v1/packs/:packId/issues; the filters work on that
// answer in the browser, so the counts at the top always speak for the whole queue.
import { SourceIssueQueue, type Principal, type SourceIssue } from "@waypoint/contracts";
import { useQueries } from "@tanstack/react-query";
import { useApi } from "../../api/provider.js";
import { Button, cn } from "@waypoint/surface";
import { Redirect, useLocation } from "wouter";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ForbiddenError } from "../../api/errors.js";
import { t } from "../../i18n/t.js";
import {
  abilitiesOf,
  guidesIn,
  matchesFilters,
  NO_FILTERS,
  withIssue,
  defaultPack,
  type Filters,
  type StatusFilter,
} from "../../issues/model.js";
import { issuesKey, useIssueQueue, usePlanCatalogue } from "../../issues/queries.js";
import { Label, Select } from "../../ui/form.js";
import { EmptyState, Page } from "../page.js";
import { AnswerPanel, type ActedOn } from "./issue-answer.js";
import { guideName, IssueChips, packName, pagesText, SegmentId } from "./issue-format.js";

const CARD = "rounded-12 border border-wp-line bg-linear-to-b from-wp-panel-2/82 to-wp-panel/74";
const KINDS = ["contradiction", "silence", "probable-typo", "pipeline-finding"] as const;
const ORIGINS = ["breakdown", "pipeline"] as const;
const STATUSES = ["open", "blocking", "answered", "waived"] as const;
const WIDE = "(min-width: 1060px)";

function Tile({
  value,
  label,
  note,
  tone,
  pressed,
  onPress,
}: {
  value: number;
  label: string;
  note: string;
  tone?: "blocking" | "answered";
  pressed: boolean;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onPress}
      className={cn(
        "grid min-w-0 flex-1 cursor-pointer grid-cols-1 items-baseline gap-x-2.5 gap-y-0.5 rounded-12 border px-2 py-1.5 text-left font-sans text-wp-text",
        "min-[721px]:min-w-37.5 min-[721px]:flex-none min-[721px]:grid-cols-[auto_1fr] min-[721px]:py-2 min-[721px]:pr-4 min-[721px]:pl-3.5",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-wp-accent",
        pressed
          ? "border-wp-accent bg-wp-accent/13"
          : "border-wp-line bg-wp-panel-2/82 hover:border-wp-accent/44",
      )}
    >
      <strong
        className={cn(
          "text-22 leading-none font-bold tabular-nums min-[721px]:text-28",
          tone === "blocking" && "text-caution",
          tone === "answered" && "text-wp-accent",
        )}
      >
        {value}
      </strong>
      <span className="text-11 font-semibold tracking-[0.04em] text-wp-text-2 uppercase min-[721px]:text-12 min-[721px]:tracking-[0.08em]">
        {label}
      </span>
      <small className="col-span-full mt-0.5 hidden text-12 text-wp-text-3 min-[721px]:block">
        {note}
      </small>
    </button>
  );
}

function Row({
  issue,
  selected,
  titleOf,
  onPick,
}: {
  issue: SourceIssue;
  selected: boolean;
  titleOf: (segmentId: string) => string | undefined;
  onPick: (id: string) => void;
}) {
  const dt = "text-11 font-semibold tracking-[0.06em] text-wp-text-3 uppercase";
  return (
    <li
      id={`row-${issue.id}`}
      data-selected={selected}
      className={cn(
        "grid gap-2 rounded-12 border px-3.5 py-3",
        selected
          ? "border-wp-accent bg-wp-accent/13 shadow-wp-glow"
          : "border-wp-line bg-wp-panel/74",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <button
          type="button"
          data-pick={issue.id}
          aria-pressed={selected}
          aria-label={t("issues.row.open", { id: issue.id })}
          onClick={() => {
            onPick(issue.id);
          }}
          className={cn(
            "cursor-pointer rounded-8 border border-wp-line-strong bg-wp-bg/58 px-2.25 py-1.25 font-mono text-13 font-semibold text-wp-text hover:border-wp-accent",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-wp-accent",
          )}
        >
          {issue.id}
        </button>
        <IssueChips issue={issue} />
      </div>
      <p className="m-0 line-clamp-2 text-14 text-wp-text">{issue.summary}</p>
      <dl className="m-0 flex flex-wrap gap-x-4.5 gap-y-1 text-12 text-wp-text-2">
        <div className="flex min-w-0 flex-wrap items-baseline gap-1.5">
          <dt className={dt}>{t("issues.meta.guide")}</dt>
          <dd className="m-0">{guideName(issue.guide)}</dd>
        </div>
        <div className="flex min-w-0 flex-wrap items-baseline gap-1.5">
          <dt className={dt}>{t("issues.meta.printed")}</dt>
          <dd className="m-0">{pagesText(issue)}</dd>
        </div>
        <div className="flex min-w-0 flex-wrap items-baseline gap-1.5">
          <dt className={dt}>{t("issues.meta.segments")}</dt>
          <dd className="m-0 flex flex-wrap gap-1">
            {issue.segments.length > 0 ? (
              issue.segments.map((id) => <SegmentId key={id} id={id} title={titleOf(id)} />)
            ) : (
              <span className="rounded-pill border border-dashed border-wp-line-strong px-2 text-11 font-semibold text-wp-text-3">
                {t("issues.noSegment")}
              </span>
            )}
          </dd>
        </div>
      </dl>
    </li>
  );
}

/** The issue named in the address (#GI-caip-12), so a link from the production plan lands on it. */
function hashId(): string | null {
  const id = decodeURIComponent(window.location.hash.slice(1));
  return id === "" ? null : id;
}

export function IssuesPage({ packId, principal }: { packId: string; principal: Principal }) {
  const queue = useIssueQueue(packId);
  const plan = usePlanCatalogue();
  const [, navigate] = useLocation();
  // Without the catalogue the page still works: this pack alone, segments by id.
  const packs =
    plan.data?.packs.includes(packId) === false
      ? [...plan.data.packs, packId]
      : (plan.data?.packs ?? [packId]);
  const titleOf = (segmentId: string) => plan.data?.titleOf(packId, segmentId);
  const abilities = abilitiesOf(principal.roles);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [selected, setSelected] = useState<string | null>(hashId);
  const [announcement, setAnnouncement] = useState("");
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onHash = () => {
      setSelected(hashId());
    };
    window.addEventListener("hashchange", onHash);
    return () => {
      window.removeEventListener("hashchange", onHash);
    };
  }, []);

  const issues = useMemo(() => queue.data?.issues ?? [], [queue.data]);
  const shown = useMemo(() => issues.filter((i) => matchesFilters(i, filters)), [issues, filters]);
  const current = issues.find((i) => i.id === selected);
  const summary = queue.data?.summary;
  const waived = issues.filter((i) => i.status === "waived").length;

  const say = useCallback((message: string) => {
    setAnnouncement("");
    window.setTimeout(() => {
      setAnnouncement(message);
    }, 30);
  }, []);

  const setStatus = (status: StatusFilter | "") => {
    setFilters((f) => ({ ...f, status }));
  };
  const pressTile = (status: StatusFilter) => {
    const next = filters.status === status ? "" : status;
    setStatus(next);
    // The count of what is listed, spoken once, as the mockup does.
    const count = issues.filter((i) => matchesFilters(i, { ...filters, status: next })).length;
    say(t("issues.announce.showing", { count }));
  };

  const pick = (id: string) => {
    setSelected(id);
    window.history.replaceState(null, "", `#${encodeURIComponent(id)}`);
    // Narrow: the panel is under the list, so take the keyboard to it. Wide: stay on the row.
    if (!window.matchMedia(WIDE).matches) {
      panelRef.current?.scrollIntoView({ block: "start" });
      requestAnimationFrame(() => document.getElementById("panel-heading")?.focus());
    }
  };

  const back = () => {
    const button = document.querySelector<HTMLElement>(`[data-pick="${selected ?? ""}"]`);
    button?.focus();
    button?.scrollIntoView({ block: "center" });
  };

  const nextOpen = () => {
    const next = shown.find((i) => i.status === "open" && i.id !== selected);
    if (next === undefined) {
      say(t("issues.panel.noMoreOpen"));
      return;
    }
    setSelected(next.id);
    window.history.replaceState(null, "", `#${encodeURIComponent(next.id)}`);
    requestAnimationFrame(() => document.getElementById("panel-heading")?.focus());
  };

  const acted = ({ act, issue }: ActedOn) => {
    // The cache already holds the API's answer; its counts are the ones to speak.
    const s = queue.data === undefined ? undefined : withIssue(queue.data, issue).summary;
    const counts = t("issues.announce.counts", {
      open: s?.open ?? 0,
      blocking: s?.blocking ?? 0,
      answered: s?.answered ?? 0,
    });
    say(
      t(
        `issues.announce.${act === "answer" ? "answered" : act === "waive" ? "waived" : "reopened"}`,
        { id: issue.id, counts },
      ),
    );
  };

  if (queue.isPending)
    return (
      <Page title={t("issues.title")}>
        <p role="status" aria-busy className="text-wp-text-3">
          {t("issues.loading")}
        </p>
      </Page>
    );
  if (queue.isError)
    return (
      <Page title={t("issues.title")}>
        <EmptyState
          icon={queue.error instanceof ForbiddenError ? "lock" : "alert"}
          title={t(
            queue.error instanceof ForbiddenError ? "issues.forbidden" : "issues.failed.title",
          )}
          {...(queue.error instanceof ForbiddenError ? {} : { line: t("issues.failed.line") })}
        >
          {queue.error instanceof ForbiddenError ? null : (
            <Button
              className="mt-3"
              onClick={() => {
                void queue.refetch();
              }}
            >
              {t("issues.failed.retry")}
            </Button>
          )}
        </EmptyState>
      </Page>
    );

  return (
    <section aria-labelledby="page-title" className="grid gap-4">
      <div
        className="sticky top-0 z-surface -mx-4 border-b border-wp-line bg-wp-bg-2/94 px-4 pt-2.5 backdrop-blur-wp-glass sm:-mx-6 sm:px-6"
        role="group"
        aria-label={t("issues.summary")}
      >
        <div className="flex flex-wrap items-stretch gap-1.5 pb-2 min-[721px]:gap-2.5 min-[721px]:pb-3">
          <Tile
            value={summary?.open ?? 0}
            label={t("issues.tile.open")}
            note={t("issues.tile.openNote")}
            pressed={filters.status === "open"}
            onPress={() => {
              pressTile("open");
            }}
          />
          <Tile
            value={summary?.blocking ?? 0}
            label={t("issues.tile.blocking")}
            note={t("issues.tile.blockingNote")}
            tone="blocking"
            pressed={filters.status === "blocking"}
            onPress={() => {
              pressTile("blocking");
            }}
          />
          <Tile
            value={summary?.answered ?? 0}
            label={t("issues.tile.answered")}
            note={t("issues.tile.answeredNote")}
            tone="answered"
            pressed={filters.status === "answered"}
            onPress={() => {
              pressTile("answered");
            }}
          />
          <span className="ml-1 hidden self-center text-13 text-wp-text-3 min-[721px]:inline">
            {waived > 0
              ? t("issues.totalWaived", { count: issues.length, waived })
              : t("issues.total", { count: issues.length })}
          </span>
        </div>
      </div>

      <div className="grid gap-1.5">
        <div className="text-12 font-semibold tracking-[0.16em] text-wp-accent uppercase">
          {t("issues.eyebrow")}
        </div>
        <h1 id="page-title" className="text-22 leading-tight font-bold">
          {t("issues.title")}
        </h1>
        <p className="m-0 max-w-[74ch] text-15 text-wp-text-2">
          {t(abilities.answer ? "issues.intro" : "issues.introReadOnly")}
        </p>
        {packs.length > 1 ? (
          <div className="mt-1.5 max-w-80">
            <Label htmlFor="pack-switch">{t("issues.pack.label")}</Label>
            <Select
              id="pack-switch"
              value={packId}
              onChange={(e) => {
                setSelected(null);
                setFilters(NO_FILTERS);
                navigate(`/issues/${encodeURIComponent(e.target.value)}`);
              }}
            >
              {packs.map((id) => (
                <option key={id} value={id}>
                  {packName(id)}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
      </div>

      {issues.length === 0 ? (
        <EmptyState icon="info" title={t("issues.empty.title")} line={t("issues.empty.line")} />
      ) : (
        <div className="grid items-start gap-5 pb-14 min-[1060px]:grid-cols-[minmax(0,1fr)_minmax(340px,440px)]">
          <section aria-labelledby="list-heading" className="min-w-0">
            <div className={CARD}>
              <h2 id="list-heading" className="px-4 pt-3.5 text-14 font-semibold">
                {t("issues.list")}
              </h2>
              <form
                role="search"
                aria-label={t("issues.filters")}
                onSubmit={(e) => {
                  e.preventDefault();
                }}
                className="grid grid-cols-2 items-end gap-2.5 px-4 pt-3.5 pb-4 min-[721px]:grid-cols-[repeat(4,minmax(0,1fr))_auto]"
              >
                <div>
                  <Label htmlFor="f-kind">{t("issues.filter.kind")}</Label>
                  <Select
                    id="f-kind"
                    value={filters.kind}
                    onChange={(e) => {
                      setFilters({ ...filters, kind: e.target.value });
                    }}
                  >
                    <option value="">{t("issues.filter.allKinds")}</option>
                    {KINDS.map((k) => (
                      <option key={k} value={k}>
                        {t(`issues.kind.${k}`)}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="f-origin">{t("issues.filter.origin")}</Label>
                  <Select
                    id="f-origin"
                    value={filters.origin}
                    onChange={(e) => {
                      setFilters({ ...filters, origin: e.target.value });
                    }}
                  >
                    <option value="">{t("issues.filter.allOrigins")}</option>
                    {ORIGINS.map((o) => (
                      <option key={o} value={o}>
                        {t(`issues.origin.${o}`)}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="f-guide">{t("issues.filter.guide")}</Label>
                  <Select
                    id="f-guide"
                    value={filters.guide}
                    onChange={(e) => {
                      setFilters({ ...filters, guide: e.target.value });
                    }}
                  >
                    <option value="">{t("issues.filter.allGuides")}</option>
                    {guidesIn(issues).map((g) => (
                      <option key={g} value={g}>
                        {guideName(g)}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="f-status">{t("issues.filter.status")}</Label>
                  <Select
                    id="f-status"
                    value={filters.status}
                    onChange={(e) => {
                      setStatus(e.target.value as StatusFilter | "");
                    }}
                  >
                    <option value="">{t("issues.filter.allStatuses")}</option>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s === "blocking" ? t("issues.filter.blocking") : t(`issues.status.${s}`)}
                      </option>
                    ))}
                  </Select>
                </div>
                <Button
                  className="col-span-full min-[721px]:col-auto"
                  onClick={() => {
                    setFilters(NO_FILTERS);
                  }}
                >
                  {t("issues.filter.clear")}
                </Button>
              </form>
              <p role="status" className="m-0 px-4 pb-3 text-13 text-wp-text-3">
                {t("issues.showing", { shown: shown.length, total: issues.length })}
              </p>
            </div>
            <ul aria-label={t("issues.listLabel")} className="m-0 mt-3.5 grid list-none gap-2 p-0">
              {shown.length > 0 ? (
                shown.map((issue) => (
                  <Row
                    key={issue.id}
                    issue={issue}
                    selected={issue.id === selected}
                    titleOf={titleOf}
                    onPick={pick}
                  />
                ))
              ) : (
                <li className={cn(CARD, "px-4 py-7 text-center text-wp-text-2")}>
                  {t("issues.noMatch")}{" "}
                  <Button
                    compact
                    onClick={() => {
                      setFilters(NO_FILTERS);
                    }}
                  >
                    {t("issues.filter.clear")}
                  </Button>
                </li>
              )}
            </ul>
          </section>

          <section
            ref={panelRef}
            aria-labelledby="panel-heading"
            className={cn(
              CARD,
              "min-w-0 min-[1060px]:sticky min-[1060px]:top-30 min-[1060px]:max-h-[calc(100vh-8.5rem)] min-[1060px]:overflow-auto",
            )}
          >
            {current === undefined ? (
              <>
                <h2 id="panel-heading" className="px-4 pt-3.5 text-18 font-semibold">
                  {t("issues.panel.empty.title")}
                </h2>
                <div className="grid gap-1.5 px-4 pt-3.5 pb-4 text-14 text-wp-text-2">
                  {abilities.answer ? (
                    <>
                      <p className="m-0">{t("issues.panel.empty.line1")}</p>
                      <p className="m-0">{t("issues.panel.empty.line2")}</p>
                    </>
                  ) : (
                    <p className="m-0">{t("issues.panel.emptyReadOnly")}</p>
                  )}
                </div>
              </>
            ) : (
              <AnswerPanel
                key={current.id}
                packId={packId}
                issue={current}
                abilities={abilities}
                titleOf={titleOf}
                onActed={acted}
                onNextOpen={
                  shown.some((i) => i.status === "open" && i.id !== current.id)
                    ? nextOpen
                    : undefined
                }
                onBack={back}
              />
            )}
          </section>
        </div>
      )}
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </section>
  );
}

/**
 * /issues with no pack named: the first pack with open issues, else the Dashboard pack. The packs
 * come from the production-plan catalogue; each one's queue is asked for its open count.
 */
export function IssuesEntry() {
  const api = useApi();
  const plan = usePlanCatalogue();
  const packs = plan.data?.packs ?? [];
  const counts = useQueries({
    queries: packs.map((id) => ({
      queryKey: issuesKey(id),
      queryFn: () => api.get(`/v1/packs/${encodeURIComponent(id)}/issues`, SourceIssueQueue),
    })),
  });
  if (plan.isPending || counts.some((c) => c.isPending))
    return (
      <Page title={t("issues.title")}>
        <p role="status" aria-busy className="text-wp-text-3">
          {t("issues.loading")}
        </p>
      </Page>
    );
  const open = new Map(packs.map((id, i) => [id, counts[i]?.data?.summary.open ?? 0]));
  // The queue's own error states (a 403, no connection) show on the page it lands on.
  return <Redirect to={`/issues/${defaultPack(packs, open)}`} replace />;
}
